import React, { useState } from 'react';
import { 
  IoStarOutline, 
  IoStar, 
  IoRocketOutline, 
  IoLocationOutline, 
  IoTrophyOutline, 
  IoBulbOutline,
  IoSendOutline,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoThumbsUpOutline,
  IoThumbsDownOutline,
  IoHappyOutline
} from 'react-icons/io5';
import './Feedback.css';

const Feedback = () => {
  const [feedback, setFeedback] = useState({
    category: '',
    rating: 0,
    title: '',
    description: '',
    email: '',
    wouldRecommend: null
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedback({ ...feedback, [name]: value });
  };

  const handleRatingClick = (rating) => {
    setFeedback({ ...feedback, rating });
  };

  const handleRecommendClick = (recommend) => {
    setFeedback({ ...feedback, wouldRecommend: recommend });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');

    try {
      // Simulate sending feedback (you can integrate with your backend later)
      await new Promise(resolve => setTimeout(resolve, 2000));
      setStatus('success');
      setFeedback({
        category: '',
        rating: 0,
        title: '',
        description: '',
        email: '',
        wouldRecommend: null
      });
    } catch (error) {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="feedback-page">
      <div className="feedback-container">
        <div className="feedback-header">
          <h1><IoStarOutline className="page-header-icon" /> Share Your Feedback</h1>
          <p>Help us improve AI Game Show! Your feedback is valuable to us.</p>
        </div>

        <div className="feedback-content">
          <div className="feedback-intro">
            <div className="intro-card">
              <h3><IoBulbOutline className="section-icon" /> Why Your Feedback Matters</h3>
              <ul>
                <li><IoRocketOutline className="list-icon" />Helps us build better features</li>
                <li><IoLocationOutline className="list-icon" />Improves your gaming experience</li>
                <li><IoTrophyOutline className="list-icon" />Shapes the future of AI Game Show</li>
                <li><IoBulbOutline className="list-icon" />Brings your ideas to life</li>
              </ul>
            </div>
            
            <div className="feedback-stats">
              <div className="stat-item">
                <span className="stat-number">500+</span>
                <span className="stat-label">Feedback Received</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">95%</span>
                <span className="stat-label">Satisfaction Rate</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">50+</span>
                <span className="stat-label">Features Improved</span>
              </div>
            </div>
          </div>

          <div className="feedback-form-container">
            <form onSubmit={handleSubmit} className="feedback-form">
              <div className="form-section">
                <h3><IoHappyOutline className="section-icon" /> Tell Us About Your Experience</h3>
                
                <div className="form-group">
                  <label>What's your feedback about?</label>
                  <select
                    name="category"
                    value={feedback.category}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    <option value="">Select a category</option>
                    <option value="gameplay">Gameplay & Mechanics</option>
                    <option value="ui-ux">User Interface & Design</option>
                    <option value="performance">Performance & Speed</option>
                    <option value="features">New Feature Request</option>
                    <option value="questions">Question Quality</option>
                    <option value="scoring">Scoring System</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Overall Rating</label>
                  <div className="rating-container">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star ${feedback.rating >= star ? 'active' : ''}`}
                        onClick={() => handleRatingClick(star)}
                        disabled={loading}
                      >
                        {feedback.rating >= star ? <IoStar /> : <IoStarOutline />}
                      </button>
                    ))}
                    <span className="rating-text">
                      {feedback.rating === 0 && "Rate your experience"}
                      {feedback.rating === 1 && "Poor"}
                      {feedback.rating === 2 && "Fair"}
                      {feedback.rating === 3 && "Good"}
                      {feedback.rating === 4 && "Very Good"}
                      {feedback.rating === 5 && "Excellent"}
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Feedback Title</label>
                  <input
                    type="text"
                    name="title"
                    value={feedback.title}
                    onChange={handleChange}
                    placeholder="Brief summary of your feedback"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label>Detailed Feedback</label>
                  <textarea
                    name="description"
                    value={feedback.description}
                    onChange={handleChange}
                    placeholder="Tell us more about your experience, suggestions, or issues..."
                    rows="5"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-section">
                <h3><IoHappyOutline className="section-icon" /> Help Us Grow</h3>
                
                <div className="form-group">
                  <label>Would you recommend AI Game Show to others?</label>
                  <div className="recommend-buttons">
                    <button
                      type="button"
                      className={`recommend-btn ${feedback.wouldRecommend === true ? 'active yes' : ''}`}
                      onClick={() => handleRecommendClick(true)}
                      disabled={loading}
                    >
                      <IoThumbsUpOutline className="recommend-icon" />
                      Yes, definitely!
                    </button>
                    <button
                      type="button"
                      className={`recommend-btn ${feedback.wouldRecommend === false ? 'active no' : ''}`}
                      onClick={() => handleRecommendClick(false)}
                      disabled={loading}
                    >
                      <IoThumbsDownOutline className="recommend-icon" />
                      Not really
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Email (Optional)</label>
                  <input
                    type="email"
                    name="email"
                    value={feedback.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com (for follow-up)"
                    disabled={loading}
                  />
                  <small>We'll only use this to follow up on your feedback if needed</small>
                </div>
              </div>

              <button type="submit" className="submit-btn" disabled={loading || feedback.rating === 0}>
                <IoSendOutline className="btn-icon" />
                {loading ? 'Sending Feedback...' : 'Submit Feedback'}
              </button>
            </form>

            {status === 'success' && (
              <div className="status-message success">
                <IoCheckmarkCircleOutline className="status-icon" />
                Thank you for your feedback! We appreciate you taking the time to help us improve.
              </div>
            )}

            {status === 'error' && (
              <div className="status-message error">
                <IoCloseCircleOutline className="status-icon" />
                Failed to submit feedback. Please try again or contact support.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback; 